import uniqid from 'uniqid';
import { getProjectState, getViewport, setProjectState } from '@starflow/app/store';
import { getSharedStage } from '@starflow/app/components/canvas/stage';
import { tryExecute } from '../utils/base';
import { copyImageToClipboard, uploadImage } from '@shakkerai/pro-utils';
import { getDefaultUnitId } from './flow';
import { groupElements, ungroupElements } from './element';

export const getCanvasStage = () => {
  return getSharedStage();
};

export const updateSelection = (selection) => {
  setProjectState({
    selection,
    editingElementId: null,
    currentElementTool: null,
  });
};

export const clearSelection = () => {
  const defaultUnitId = getDefaultUnitId();
  setProjectState({
    selection: [],
    editingElementId: null,
    currentElementTool: null,
    currentNodeId: defaultUnitId ?? null,
  });
};

export const insertElement = (elementData, options) => {
  return insertElements([elementData], options)[0];
};

export const insertElements = (elementDatas, options) => {
  const elements = getProjectState('elements');
  const insertElementIds = [];

  setProjectState(
    {
      elements: elements.concat(
        elementDatas.map((elementData) => {
          const { type, x, y, width, height, scaleX, scaleY, rotation, data, id, ...rest } = elementData;
          const newId = typeof id === 'number' || typeof id === 'undefined' ? `element-${type}-${uniqid()}` : id;

          insertElementIds.push(newId);

          return {
            id: newId,
            type,
            x: x || 0,
            y: y || 0,
            width: width || 1,
            height: height || 1,
            scaleX: scaleX || 1,
            scaleY: scaleY || 1,
            rotation: rotation || 0,
            data: data || {},
            ...rest,
          };
        }),
      ),
    },
    options,
  );

  return insertElementIds;
};

export const updateElement = (id, partialElement, options) => {
  const elements = getProjectState('elements');

  setProjectState(
    {
      elements: elements.map((element) => {
        if (element.id === id) {
          const updatedElement = tryExecute(partialElement, element);

          return {
            ...element,
            ...updatedElement,
          };
        }

        return element;
      }),
    },
    options,
  );
};

export const updateElementData = (elementId, partialData, options) => {
  updateElement(
    elementId,
    (element) => {
      const updatedData = tryExecute(partialData, element.data, element);

      return {
        data: {
          ...element.data,
          ...updatedData,
        },
      };
    },
    options,
  );
};

export const removeElements = (elementIds) => {
  const elements = getProjectState('elements');

  // TODO
  // 这里？似乎需要将元素相关的独立工作单元也删除掉？

  setProjectState({
    selection: [],
    elements: elements.filter((element) => !elementIds.includes(element.id)),
  });
};

export const copyElement = (elementId) => {
  const elements = getProjectState('elements');
  const targetElement = elements.find((element) => element.id === elementId);
  copyImageToClipboard(targetElement.url);
  // clearContextMenu();
};

export const moveUpElement = (elementId) => {
  const elements = getProjectState('elements');
  const index = elements.findIndex((element) => element.id === elementId);

  if (index === -1 || index === elements.length - 1) {
    return;
  }

  const newElements = [...elements];
  [newElements[index], newElements[index + 1]] = [newElements[index + 1], newElements[index]];

  setProjectState({
    elements: newElements,
  });
};

export const moveDownElement = (elementId) => {
  const elements = getProjectState('elements');
  const index = elements.findIndex((element) => element.id === elementId);

  if (index === -1 || index === 0) {
    return;
  }

  const newElements = [...elements];
  [newElements[index], newElements[index - 1]] = [newElements[index - 1], newElements[index]];

  setProjectState({
    elements: newElements,
  });
};

export const moveElementToTop = (elementId) => {
  const elements = getProjectState('elements');
  const index = elements.findIndex((element) => element.id === elementId);

  if (index === -1 || index === elements.length - 1) {
    return;
  }

  const newElements = [...elements];
  newElements.push(newElements.splice(index, 1)[0]);

  setProjectState({
    elements: newElements,
  });
};

export const moveElementToBottom = (elementId) => {
  const elements = getProjectState('elements');
  const index = elements.findIndex((element) => element.id === elementId);

  if (index === -1 || index === 0) {
    return;
  }

  const newElements = [...elements];
  newElements.unshift(newElements.splice(index, 1)[0]);

  setProjectState({
    elements: newElements,
  });
};

export const addELementsToGroup = (groupId, elements) => {};

export const removeElementsFromGroup = (groupId, elements) => {};

export const updateSelectionFrame = () => {
  getSharedStage().findOne('#mainTransformer')?.forceUpdate();
};

export const convertLegacyCanvasData = (data) => {
  const fixedElements = (data.elements ?? []).map((item) => {
    if (item.type === 'image') {
      item.id = item.id.replace(`elementImage`, `element-image-`);
    }

    if (item.type === 'group') {
      item.id = item.id.replace(`elementGroup`, `element-group-`);
    }

    return item;
  });

  Object.keys(data.tasks?.map || {}).forEach((key) => {
    if (data.tasks.map[key]?.status === 'idle') {
      delete data.tasks.map[key];
    }
  });

  delete data.generators;
  delete data.taskQueue;
  delete data.taskMap;
  delete data.backgroundColor;
  delete data.activeSmartRemove;
  delete data.activeRemove;
  delete data.activeCrop;
  delete data.nodeMap;
  delete data.units;
  delete data.flows;
  delete data.viewport?.canvasSize;

  return { elements: fixedElements, ...data };
};

export const getCanvasCenter = () => {
  const viewport = getViewport();
  const sharedStage = getCanvasStage();

  if (!sharedStage) {
    return { x: 0, y: 0 };
  }

  const { width: canvasWidth, height: canvasHeight } = sharedStage.attrs;
  const centerX = canvasWidth / 2;
  const centerY = canvasHeight / 2;

  let x = centerX / viewport.scale - viewport.x / viewport.scale;
  let y = centerY / viewport.scale - viewport.y / viewport.scale;

  return { x, y };
};

export const selectionToFile = async () => {
  const selection = getProjectState('selection');
  const { scale } = getViewport();
  let groupId, tempGroup;
  if (selection.length === 1 && selection[0].includes('group')) {
    groupId = selection[0];
  } else {
    tempGroup = true;
    const [elements, group] = groupElements(selection);
    setProjectState({ elements: elements, selection: [] });
    await new Promise((resolve) => requestAnimationFrame(resolve));
    groupId = group.id;
  }
  const konvaStage = getSharedStage();
  const groupNode = konvaStage.findOne(`#${groupId}`);
  const blob = await groupNode.toBlob({ pixelRatio: 1 / scale });
  const file = new File([blob], 'temp.png', { type: 'image/png' });
  const result = await uploadImage(file);
  const rect = groupNode.getClientRect();
  if (tempGroup) {
    const newElements = ungroupElements([groupId]);
    setProjectState({ elements: newElements, selection });
  }
  return {
    rect,
    file: result,
    localFile: file,
    blob: blob,
  };
};
